// Flashcard session — PRD §5.4
//   - Session queue: new cards first, then due cards (nextReviewDate <= today),
//     oldest due first.
//   - One card at a time. Audio button + auto-attempted playback. 1/2/3 keys
//     map to Again/Hard/Got it. Space replays audio.
//   - End-of-session summary shows totals + next due date.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { api, type ApiError } from '../api/client';
import type { Phrase, ProgressResponse, Rating, SessionData } from '../types';

interface RatedItem {
  phrase: Phrase;
  rating: Rating;
  next: ProgressResponse;
}

function todayUtcMidnight(): Date {
  const t = new Date();
  t.setUTCHours(0, 0, 0, 0);
  return t;
}

function buildQueue(phrases: Phrase[]): Phrase[] {
  const today = todayUtcMidnight().getTime();
  const newCards = phrases.filter((p) => !p.progress);
  const dueCards = phrases
    .filter((p) => p.progress?.nextReviewDate && new Date(p.progress.nextReviewDate).getTime() <= today)
    .sort((a, b) => {
      const ad = new Date(a.progress!.nextReviewDate!).getTime();
      const bd = new Date(b.progress!.nextReviewDate!).getTime();
      return ad - bd;
    });
  return [...newCards, ...dueCards];
}

function formatNextDue(items: RatedItem[]): string | null {
  const dates = items
    .map((it) => it.next.nextReviewDate)
    .filter((d): d is string => Boolean(d))
    .map((d) => new Date(d).getTime());
  if (dates.length === 0) return null;
  const earliest = Math.min(...dates);
  const diffDays = Math.round((earliest - todayUtcMidnight().getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return 'today';
  if (diffDays === 1) return 'tomorrow';
  return `in ${diffDays} days`;
}

export function SessionPage() {
  const { categorySlug = '' } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState<SessionData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [index, setIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [rated, setRated] = useState<RatedItem[]>([]);

  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Load
  useEffect(() => {
    let cancelled = false;
    api<SessionData>(`/api/phrases/${encodeURIComponent(categorySlug)}`)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (cancelled) return;
        const e = err as ApiError;
        if (e.status === 403) setLoadError('This category is part of Pro.');
        else if (e.status === 404) setLoadError('Category not found.');
        else setLoadError(e.message ?? 'Failed to load session.');
      });
    return () => {
      cancelled = true;
    };
  }, [categorySlug]);

  const queue = useMemo(() => (data ? buildQueue(data.phrases) : []), [data]);
  const current = queue[index] ?? null;
  const total = queue.length;
  const sessionDone = total > 0 && index >= total;

  // Try autoplay when card changes (browsers may block on mobile — silent fail).
  useEffect(() => {
    if (!current) return;
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = 0;
    void audio.play().catch(() => {
      /* autoplay blocked; user can press the audio button */
    });
  }, [current?.id]);

  const submitRating = useCallback(
    async (rating: Rating) => {
      if (!current || submitting) return;
      setSubmitting(true);
      try {
        const next = await api<ProgressResponse>('/api/progress', {
          method: 'POST',
          body: JSON.stringify({ phraseId: current.id, rating }),
        });
        setRated((prev) => [...prev, { phrase: current, rating, next }]);
        setIndex((i) => i + 1);
      } catch (err) {
        console.error(err);
        // Stay on the card; user can retry. (Could surface an inline error later.)
      } finally {
        setSubmitting(false);
      }
    },
    [current, submitting],
  );

  // Keyboard shortcuts
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (sessionDone) return;
      if (e.key === '1') {
        e.preventDefault();
        void submitRating(0);
      } else if (e.key === '2') {
        e.preventDefault();
        void submitRating(1);
      } else if (e.key === '3') {
        e.preventDefault();
        void submitRating(2);
      } else if (e.key === ' ') {
        e.preventDefault();
        const audio = audioRef.current;
        if (audio) {
          audio.currentTime = 0;
          void audio.play().catch(() => {});
        }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [submitRating, sessionDone]);

  // ----- UI states ----------------------------------------------------------
  if (loadError) {
    return (
      <div className="page-content">
        <div className="form-error">{loadError}</div>
        <Link to="/" className="btn-secondary" style={{ display: 'inline-block', marginTop: 16 }}>
          Back to dashboard
        </Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="page-content">
        <p style={{ color: 'var(--text-muted)' }}>Loading session…</p>
      </div>
    );
  }

  if (total === 0) {
    return (
      <div className="page-content">
        <h1>{data.category.name}</h1>
        <p style={{ color: 'var(--text-secondary)' }}>
          You're all caught up — no cards due right now. Come back tomorrow, or pick another category.
        </p>
        <Link to="/" className="btn-primary" style={{ display: 'inline-block', marginTop: 16 }}>
          Back to dashboard
        </Link>
      </div>
    );
  }

  if (sessionDone) {
    const newCount = rated.filter((r) => r.next.reviewCount === 1).length;
    const nextDue = formatNextDue(rated);
    return (
      <div className="page-content session-summary">
        <h1>Session complete</h1>
        <p>{rated.length} cards reviewed{newCount > 0 && ` · ${newCount} new`}</p>
        {nextDue && <p style={{ color: 'var(--text-secondary)' }}>Next cards due {nextDue}.</p>}
        <Link to="/" className="btn-primary" style={{ display: 'inline-block', marginTop: 16 }}>
          Back to dashboard
        </Link>
      </div>
    );
  }

  // ----- Active card --------------------------------------------------------
  if (!current) return null;
  const progress = ((index + 1) / total) * 100;

  return (
    <div className="session-layout">
      <div className="session-header">
        <Link to="/" className="btn-link" aria-label="Back to dashboard">← Exit</Link>
        <span className="session-counter">
          {index + 1} of {total}
        </span>
      </div>
      <div className="progress-track">
        <div className="progress-fill" style={{ width: `${progress}%` }} />
      </div>

      <article className="flashcard">
        {current.situationTag && <span className="situation-tag">{current.situationTag}</span>}
        <div className="phrase-vietnamese">{current.vietnamese}</div>
        <div className="phrase-phonetic">{current.phonetic}</div>
        <div className="phrase-english">{current.english}</div>
        <button
          type="button"
          className="audio-btn"
          aria-label="Play audio"
          onClick={() => {
            const a = audioRef.current;
            if (a) {
              a.currentTime = 0;
              void a.play().catch(() => {});
            }
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M8 5v14l11-7z" />
          </svg>
        </button>
        <audio ref={audioRef} src={`/audio/${current.audioFile}`} preload="auto" />
        {current.contextNote && (
          <details className="context-note">
            <summary>Context</summary>
            <p>{current.contextNote}</p>
          </details>
        )}
      </article>

      <div className="response-buttons">
        <button
          type="button"
          className="response-btn btn-again"
          onClick={() => void submitRating(0)}
          disabled={submitting}
        >
          Again
        </button>
        <button
          type="button"
          className="response-btn btn-hard"
          onClick={() => void submitRating(1)}
          disabled={submitting}
        >
          Hard
        </button>
        <button
          type="button"
          className="response-btn btn-gotit"
          onClick={() => void submitRating(2)}
          disabled={submitting}
        >
          Got it
        </button>
      </div>

      <p className="session-hint">Keys: 1 / 2 / 3 to rate · Space to replay audio</p>
    </div>
  );
}
